import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Typography, Divider, Progress } from 'antd';
import { 
  SafetyCertificateOutlined, 
  LineChartOutlined, 
  MonitorOutlined, 
  SafetyOutlined, 
  BugOutlined, 
  SettingOutlined,
  UserOutlined,
  LoginOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  StarFilled,
  DashboardOutlined,
  BarChartOutlined,
  TableOutlined,
  EyeOutlined,
  ControlOutlined
} from '@ant-design/icons';
import { isUserAuthenticated, getUserRoleFromToken } from '../utils/auth';
import './HomePage.css';

// 导入本地图片
import userhomeImg from '../img/userhome.jpg';
import llcxImg from '../img/llcx.jpg';
import glgzImg from '../img/glgz.jpg';
import ramImg from '../img/ram.jpg';

const { Title, Paragraph } = Typography;

const HomePage = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    
    // 触发入场动画
    setTimeout(() => setIsVisible(true), 100);
    
    // 检查用户登录状态
    const checkAuthStatus = () => {
      const authenticated = isUserAuthenticated();
      setIsLoggedIn(authenticated);
      
      if (authenticated) {
        setUserRole(getUserRoleFromToken());
      }
    };
    
    checkAuthStatus();
    
    // 监听storage变化（当用户登录或登出时）
    const handleStorageChange = () => {
      checkAuthStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const features = [
    {
      icon: <SafetyCertificateOutlined style={{ fontSize: '48px' }} />,
      title: 'DDoS防护',
      description: '专业的DDoS攻击防护系统，实时监控网络流量，有效防御各类网络攻击。',
      color: '#1890ff',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      icon: <LineChartOutlined style={{ fontSize: '48px' }} />,
      title: '流量监控',
      description: '实时监控IP流量状态，提供详细的流量分析图表和统计数据。',
      color: '#52c41a',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
    },
    {
      icon: <MonitorOutlined style={{ fontSize: '48px' }} />,
      title: '攻击检测',
      description: '智能检测网络攻击行为，记录攻击详情，提供完整的安全日志。',
      color: '#faad14',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      icon: <SafetyOutlined style={{ fontSize: '48px' }} />,
      title: '策略管理',
      description: '灵活的IP策略配置，支持黑白名单管理和自定义防护规则。',
      color: '#f5222d',
      gradient: 'linear-gradient(135deg, #fc466b 0%, #3f5efb 100%)'
    },
    {
      icon: <BugOutlined style={{ fontSize: '48px' }} />,
      title: '故障报告',
      description: '完善的故障报告系统，快速响应用户反馈，持续优化系统性能。',
      color: '#722ed1',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    },
    {
      icon: <SettingOutlined style={{ fontSize: '48px' }} />,
      title: '系统管理',
      description: '强大的系统管理功能，支持用户管理、权限控制和系统配置。',
      color: '#13c2c2',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }
  ];

  const stats = [
    { value: '99.9%', label: '系统可用性', color: '#1890ff' },
    { value: '24/7', label: '监控服务', color: '#52c41a' },
    { value: '1000+', label: '防护IP', color: '#faad14' },
    { value: '1TB+', label: '清洗带宽', color: '#f5222d' }
  ];

  const systemPages = [
    {
      title: '用户首页',
      description: '个人仪表板，一站式查看IP防护状态、流量概览和系统通知',
      icon: <DashboardOutlined />,
      features: ['数据总览', '防护状态', '实时监控'],
      bgGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      image: userhomeImg
    },
    {
      title: '流量查询页面',
      description: '详细的流量数据查询和分析，支持多维度数据统计和图表展示',
      icon: <BarChartOutlined />,
      features: ['流量统计', '趋势分析', '数据导出'],
      bgGradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      image: llcxImg
    },
    {
      title: '过滤规则页面',
      description: '自定义IP过滤规则，配置封UDP和封海外防护策略',
      icon: <TableOutlined />,
      features: ['规则配置', '策略管理', '个性配置'],
      bgGradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      image: glgzImg
    },
    {
      title: 'RAM账户管理页面',
      description: '子账户权限管理，分配访问权限和资源控制设置',
      icon: <EyeOutlined />,
      features: ['权限分配', '子账户管理', '访问控制'],
      bgGradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      image: ramImg
    }
  ];

  return (
    <div className="homepage-container">
      {/* 动态背景 */}
      <div className="animated-background">
        <div className="floating-shapes">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`shape shape-${i + 1}`} />
          ))}
        </div>
      </div>
      
      {/* Hero Section */}
      <section 
        className="hero-section" 
        style={{ transform: `translateY(${scrollY * 0.5}px)` }}
      >
        <div className="hero-content">
          <div className={`hero-text ${isVisible ? 'fade-in' : ''}`}>
            <Title 
              level={1} 
              className="hero-title"
            >
              DDoS流量监控系统
            </Title>
            <Paragraph className="hero-subtitle">
              专业的网络安全防护平台，为您的网络基础设施提供全方位的DDoS攻击防护和流量监控服务
            </Paragraph>
            <div className="hero-buttons">
              {isLoggedIn ? (
                // 已登录用户显示进入控制台按钮
                <Button 
                  type="primary" 
                  size="large" 
                  icon={<ControlOutlined />}
                  className="primary-button"
                  onClick={() => {
                    const targetPath = userRole === 'admin' ? '/admin/dashboard' : '/user/dashboard';
                    navigate(targetPath);
                  }}
                >
                  进入控制台
                </Button>
              ) : (
                // 未登录用户显示登录和注册按钮
                <>
                  <Button 
                    type="primary" 
                    size="large" 
                    icon={<LoginOutlined />}
                    className="primary-button"
                    onClick={() => navigate('/login')}
                  >
                    立即登录
                  </Button>
                  <Button 
                    size="large" 
                    className="secondary-button"
                    icon={<UserOutlined />}
                    onClick={() => navigate('/register')}
                  >
                    申请账号
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* 统计数据条 */}
      <section className="stats-bar">
        <div className="container">
          <Row gutter={[32, 16]} justify="center">
            {stats.map((stat, index) => (
              <Col xs={12} sm={6} key={index}>
                <div className="stat-item">
                  <div className="stat-value" style={{ color: stat.color }}>
                    {stat.value}
                  </div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <Title level={2} className="section-title">核心功能</Title>
            <Paragraph className="section-subtitle">
              全面的网络安全解决方案，保护您的数字资产
            </Paragraph>
          </div>

          <Row gutter={[32, 32]}>
            {features.map((feature, index) => (
              <Col xs={24} md={12} lg={8} key={index}>
                <Card 
                  className={`feature-card feature-card-${index + 1}`}
                  bodyStyle={{ padding: 0 }}
                  hoverable
                >
                  <div 
                    className="feature-card-inner"
                    style={{ '--gradient': feature.gradient }}
                  >
                    <div className="feature-icon" style={{ color: feature.color }}>
                      {feature.icon}
                    </div>
                    <Title level={4} className="feature-title">
                      {feature.title}
                    </Title>
                    <Paragraph className="feature-description">
                      {feature.description}
                    </Paragraph>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* System Pages Preview Section */}
      <section className="system-pages-section">
        <div className="container">
          <div className="section-header">
            <Title level={2} className="section-title">系统页面预览</Title>
            <Paragraph className="section-subtitle">
              探索强大的系统功能界面，体验专业的管理工具
            </Paragraph>
          </div>

          <Row gutter={[32, 32]}>
            {systemPages.map((page, index) => (
              <Col xs={24} lg={12} key={index}>
                <Card 
                  className={`system-page-card system-page-card-${index + 1}`}
                  bodyStyle={{ padding: 0 }}
                  hoverable
                >
                  <div className="system-page-image-container">
                    <div className="system-page-image-overlay" style={{ background: page.bgGradient }}>
                      <div className="system-page-icon">
                        {page.icon}
                      </div>
                    </div>
                    <img 
                      src={page.image} 
                      alt={page.title}
                      className="system-page-image"
                    />
                  </div>
                  
                  <div className="system-page-content">
                    <Title level={4} className="system-page-title">
                      {page.title}
                    </Title>
                    <Paragraph className="system-page-description">
                      {page.description}
                    </Paragraph>
                    
                    <div className="system-page-features">
                      {page.features.map((feature, fIndex) => (
                        <div key={fIndex} className="system-page-feature-tag">
                          <CheckCircleOutlined />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    

                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="container">
          <Row gutter={[64, 64]} align="middle">
            <Col xs={24} lg={12}>
              <div className="about-content">
                <Title level={2} className="about-title">关于系统</Title>
                <Paragraph className="about-text">
                  DDoS流量监控系统是一款专业的网络安全防护平台，致力于为企业和个人用户提供
                  全面的DDoS攻击防护服务。系统具备实时流量监控、智能攻击检测、灵活策略配置
                  等核心功能。
                </Paragraph>
                <Paragraph className="about-text">
                  我们的系统支持大墙和小墙双重防护架构，能够有效应对各种规模的网络攻击，
                  为您的网络基础设施提供坚实的安全保障。
                </Paragraph>
                <div className="feature-list">
                  <div className="feature-item">
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                    <span>7x24小时监控</span>
                  </div>
                  <div className="feature-item">
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                    <span>智能攻击识别</span>
                  </div>
                  <div className="feature-item">
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                    <span>快速响应处理</span>
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div className="stats-visual">
                <div className="stats-card">
                  <Title level={3} style={{ color: 'white', textAlign: 'center', marginBottom: '2rem' }}>
                    实时系统状态
                  </Title>
                  <div className="progress-items">
                    <div className="progress-item">
                      <div className="progress-label">
                        <span>系统负载</span>
                        <span>23%</span>
                      </div>
                      <Progress percent={23} strokeColor="#52c41a" />
                    </div>
                    <div className="progress-item">
                      <div className="progress-label">
                        <span>防护效率</span>
                        <span>99.9%</span>
                      </div>
                      <Progress percent={99.9} strokeColor="#1890ff" />
                    </div>
                    <div className="progress-item">
                      <div className="progress-label">
                        <span>响应时间</span>
                        <span>15ms</span>
                      </div>
                      <Progress percent={85} strokeColor="#faad14" />
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <Title level={2} className="cta-title">
              开始使用DDoS流量监控系统
            </Title>
            <Paragraph className="cta-subtitle">
              立即注册账号，体验专业的网络安全防护服务
            </Paragraph>
            <div className="cta-buttons">
              <Button 
                type="primary" 
                size="large"
                className="cta-primary-button"
                icon={<StarFilled />}
                onClick={() => navigate('/register')}
              >
                免费申请账号
              </Button>
              <Button 
                size="large"
                className="cta-secondary-button"
                icon={<ArrowRightOutlined />}
                onClick={() => navigate('/login')}
              >
                已有账号登录
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <Divider style={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
          <Paragraph className="footer-text">
            © 2025 南京思顿软件科技有限公司. 保留所有权利.
          </Paragraph>
        </div>
      </footer>
    </div>
  );
};

export default HomePage; 